<template>
    <v-dialog v-model="dialog" max-width="1300px" persistent @keydown.esc="handleEscClose">
        <v-card class="d-flex flex-column" style="max-height: 90vh;">
            <!-- Titolo fisso -->
            <v-card-title class="headline d-flex align-center justify-space-between">
                Gestione prodotto in uscita
                <v-btn icon @click="handleEscClose">
                    <v-icon>close</v-icon>
                </v-btn>
            </v-card-title>

            <v-divider />

            <!-- Corpo scorrevole -->
            <v-card-text class="flex-grow-1" style="overflow-y: auto; max-height: calc(90vh - 80px);">
                <v-form ref="form" v-model="formValid">
                    <div class="mt-4 mb-4">
                        <v-text-field label="Riferimento" v-model="elemento.riferimento" :rules="[rules.required]"
                            outlined dense />
                        <v-text-field label="Cliente" v-model="elemento.clienteUscita" :rules="[rules.required]"
                            outlined dense hide-details />
                    </div>

                    <div>
                        <!-- Ogni riga di output e' un blocco -->
                        <div v-for="(sezione, index) in elemento.sezioni" :key="sezione.id"
                            style="border: solid 1px #e0e0e0; padding: 16px; margin-bottom: 20px; border-radius: 4px;">
                            <div class="d-flex justify-space-between align-center mb-2">
                                <h2 class="text-h5">Gruppo di prodotto {{ index + 1 }}</h2>
                                <v-btn icon color="error" @click="rimuoviSezione(index)"
                                    v-tooltip.top="'Elimina gruppo di prodotto ' + (index + 1)">
                                    <v-icon>delete</v-icon>
                                </v-btn>
                            </div>

                            <div>
                                <div class="text-subtitle-2 mb-2">Inserire un gruppo di prodotto da registrare</div>
                                <v-row>
                                    <v-col cols="12" sm="6">
                                        <v-autocomplete label="Gruppo di prodotto" v-model="sezione.gruppoCategoriaFSC"
                                            :items="gruppiOutput"
                                            :item-text="item => item.codGruppo + ' - ' + item.traduzione"
                                            item-value="codGruppo" return-object :rules="[rules.required]" outlined
                                            dense hide-details />
                                    </v-col>
                                    <v-col cols="12" sm="3">
                                        <v-select label="Dichiarazione FSC" v-model="sezione.dichiarazioneFSC"
                                            :items="dichiarazioniFSC" item-text="descrizione"
                                            item-value="idDichiarazione" return-object :rules="[rules.required]"
                                            outlined dense hide-details />
                                    </v-col>
                                    <v-col cols="12" sm="3">
                                        <v-text-field label="Data" v-model="sezione.dataUscita" type="date"
                                            :rules="[rules.required]" outlined dense hide-details />
                                    </v-col>
                                </v-row>

                                <!-- LEGNO: sezione e ml prodotti -->
                                <v-row v-if="variante === 'LEGNO'">
                                    <v-col cols="12" sm="3">
                                        <v-autocomplete :items="essenze" label="Essenza" v-model="sezione.essenza"
                                            outlined dense hide-details />
                                    </v-col>
                                    <v-col cols="6" sm="2">
                                        <v-text-field label="Sezione base (mm)" v-model.number="sezione.sezioneBase"
                                            type="number" min="0" outlined dense hide-details />
                                    </v-col>
                                    <v-col cols="6" sm="2">
                                        <v-text-field label="Sezione altezza (mm)"
                                            v-model.number="sezione.sezioneAltezza" type="number" min="0" outlined
                                            dense hide-details />
                                    </v-col>
                                    <v-col cols="6" sm="3">
                                        <v-text-field label="Quantità prodotta" v-model.number="sezione.quantita"
                                            type="number" min="0" :rules="[rules.required, rules.numeroPositivo]"
                                            outlined dense hide-details hint="ml prodotti (con sezione indicata)"
                                            persistent-hint />
                                    </v-col>
                                    <v-col cols="6" sm="2">
                                        <v-autocomplete :items="um" label="Unità Misura" v-model="sezione.um"
                                            :rules="[rules.required]" outlined dense hide-details />
                                    </v-col>
                                </v-row>

                                <!-- CARTONE: codici, quantita' prodotte e resa -->
                                <v-row v-else>
                                    <v-col cols="12" sm="3">
                                        <v-text-field label="Codice prodotto" v-model="sezione.codiceProdotto" outlined
                                            dense hide-details />
                                    </v-col>
                                    <v-col cols="12" sm="3">
                                        <v-text-field label="Codice impianto stampa"
                                            v-model="sezione.codiceImpiantoStampa" outlined dense hide-details />
                                    </v-col>
                                    <v-col cols="6" sm="2">
                                        <v-text-field label="Quantità prodotte" v-model.number="sezione.quantita"
                                            type="number" min="0" :rules="[rules.required, rules.numeroPositivo]"
                                            outlined dense hide-details />
                                    </v-col>
                                    <v-col cols="6" sm="2">
                                        <v-text-field label="Resa" v-model.number="sezione.resa" type="number"
                                            min="0" step="any" :rules="[rules.required, rules.numeroPositivo]" outlined
                                            dense hide-details hint="scatole per foglio" persistent-hint />
                                    </v-col>
                                    <v-col cols="12" sm="2">
                                        <v-autocomplete :items="um" label="Unità Misura" v-model="sezione.um"
                                            :rules="[rules.required]" outlined dense hide-details />
                                    </v-col>
                                </v-row>

                                <!-- Anteprima del calcolo (le formule definitive sono sul server) -->
                                <div class="d-flex flex-wrap mt-4 pa-3 grey lighten-4 rounded"
                                    v-if="righeCalcolo(sezione).length">
                                    <div v-for="r in righeCalcolo(sezione)" :key="r.label" class="mr-8">
                                        <span class="text-caption text--secondary">{{ r.label }}</span><br>
                                        <strong :class="{ 'error--text': r.warn }">{{ r.value }}</strong>
                                    </div>
                                </div>
                                <div class="text-caption text--secondary mt-1" v-if="noteCalcolo(sezione)">
                                    {{ noteCalcolo(sezione) }}
                                </div>
                            </div>

                            <!-- Materiale prelevato -->
                            <div class="mt-6">
                                <v-divider class="mb-4" />
                                <h3 class="text-h6 mb-2">Materiale {{ index + 1 }}</h3>
                                <div class="text-subtitle-2 mb-2">
                                    Selezionare dalla tabella le materie prime utilizzate e impostare la quantità
                                    prelevata
                                </div>
                                <v-data-table :headers="headers" :items="sezione.elencoMateriePrime"
                                    item-key="idMateriale" show-select v-model="sezione.selected" class="elevation-1"
                                    fixed-header height="250px" hide-default-footer :items-per-page="-1"
                                    @input="aggiornaPrelievi(sezione)">
                                    <template v-slot:item.disponibile="{ item }">
                                        {{ disponibile(item, sezione) }}
                                    </template>
                                    <template v-slot:item.qtaPrelievo="{ item }">
                                        <v-text-field v-model.number="item.qtaPrelievo" type="number" dense
                                            hide-details outlined min="0" :max="disponibile(item, sezione)"
                                            style="max-width: 110px" @change="validaPrelievo(item, sezione)" />
                                    </template>
                                </v-data-table>
                            </div>
                        </div>

                        <v-btn @click="aggiungiSezione" class="mb-4 elevation-2" color="primary" outlined>
                            <v-icon left>add</v-icon>
                            Aggiungi gruppo di prodotto
                        </v-btn>
                    </div>

                    <!-- DDT / Fatture clienti -->
                    <div class="mt-6">
                        <v-divider class="mb-4" />
                        <h2 class="text-h5 mb-4">DDT / Fatture clienti</h2>

                        <div class="text-subtitle-2 mb-4">
                            Inserire i dati di eventuali DDT e fatture dei clienti
                        </div>

                        <v-card class="mb-6" outlined>
                            <v-card-title class="py-2">
                                <span class="text-h6">DDT</span>
                                <v-spacer />
                                <v-btn icon color="primary" @click="addDDT">
                                    <v-icon>add</v-icon>
                                </v-btn>
                            </v-card-title>

                            <v-data-table :headers="headersDDT" :items="elemento.ddt" item-key="LocalId"
                                class="elevation-0" disable-pagination fixed-header height="250px" hide-default-footer
                                :items-per-page="-1" no-data-text="Nessun DDT presente">
                                <template v-slot:item.numBolla="{ item }">
                                    <v-text-field v-model="item.numBolla" dense hide-details outlined />
                                </template>
                                <template v-slot:item.dataBolla="{ item }">
                                    <v-text-field v-model="item.dataBolla" type="date" dense hide-details outlined />
                                </template>
                                <template v-slot:item.descrizione="{ item }">
                                    <v-text-field v-model="item.descrizione" dense hide-details outlined />
                                </template>
                                <template v-slot:item.actions="{ item }">
                                    <v-btn icon color="error" @click="removeDDT(item)">
                                        <v-icon>delete</v-icon>
                                    </v-btn>
                                </template>
                            </v-data-table>
                        </v-card>

                        <v-card outlined>
                            <v-card-title class="py-2">
                                <span class="text-h6">Fatture</span>
                                <v-spacer />
                                <v-btn icon color="primary" @click="addFattura">
                                    <v-icon>add</v-icon>
                                </v-btn>
                            </v-card-title>

                            <v-data-table :headers="headersFatture" :items="elemento.fatture" item-key="LocalId"
                                class="elevation-0" disable-pagination fixed-header height="250px" hide-default-footer
                                :items-per-page="-1" no-data-text="Nessuna fattura presente">
                                <template v-slot:item.numDocumento="{ item }">
                                    <v-text-field v-model="item.numDocumento" dense hide-details outlined />
                                </template>
                                <template v-slot:item.dataDocumento="{ item }">
                                    <v-text-field v-model="item.dataDocumento" type="date" dense hide-details
                                        outlined />
                                </template>
                                <template v-slot:item.note="{ item }">
                                    <v-text-field v-model="item.note" dense hide-details outlined />
                                </template>
                                <template v-slot:item.actions="{ item }">
                                    <v-btn icon color="error" @click="removeFattura(item)">
                                        <v-icon>delete</v-icon>
                                    </v-btn>
                                </template>
                            </v-data-table>
                        </v-card>
                    </div>

                    <!-- Inviato a terzisti -->
                    <div class="mt-6">
                        <v-divider class="mb-4" />
                        <h2 class="text-h5 mb-4">Inviato a terzista</h2>
                        <div class="text-subtitle-2 mb-4">
                            Inserire i dati relativi ai terzisti
                        </div>
                        <v-card outlined>
                            <v-card-title class="py-2">
                                <span class="text-h6">Inviato a terzista</span>
                                <v-spacer />
                                <v-btn icon color="primary" @click="addTerzista('inv')">
                                    <v-icon>add</v-icon>
                                </v-btn>
                            </v-card-title>

                            <v-data-table :headers="headersTerzisti" :items="elemento.terzistiInv" item-key="LocalId"
                                class="elevation-0" disable-pagination hide-default-footer
                                no-data-text="Nessun terzista presente">
                                <template v-slot:item.fornitore="{ item }">
                                    <v-text-field v-model="item.fornitore" dense hide-details outlined />
                                </template>
                                <template v-slot:item.numBolla="{ item }">
                                    <v-text-field v-model="item.numBolla" dense hide-details outlined />
                                </template>
                                <template v-slot:item.dataBolla="{ item }">
                                    <v-text-field v-model="item.dataBolla" type="date" dense hide-details outlined />
                                </template>
                                <template v-slot:item.descrizione="{ item }">
                                    <v-text-field v-model="item.descrizione" dense hide-details outlined />
                                </template>
                                <template v-slot:item.actions="{ item }">
                                    <v-btn icon color="error" @click="removeTerzista('inv', item)">
                                        <v-icon>delete</v-icon>
                                    </v-btn>
                                </template>
                            </v-data-table>
                        </v-card>
                    </div>

                    <!-- Ricevuto da terzisti -->
                    <div class="mt-6">
                        <v-divider class="mb-4" />
                        <h2 class="text-h5 mb-4">Ricevuto da terzista</h2>
                        <div class="text-subtitle-2 mb-4">
                            Inserire i dati relativi ai terzisti
                        </div>
                        <v-card outlined>
                            <v-card-title class="py-2">
                                <span class="text-h6">Ricevuto da terzista</span>
                                <v-spacer />
                                <v-btn icon color="primary" @click="addTerzista('ric')">
                                    <v-icon>add</v-icon>
                                </v-btn>
                            </v-card-title>

                            <v-data-table :headers="headersTerzisti" :items="elemento.terzistiRic" item-key="LocalId"
                                class="elevation-0" disable-pagination hide-default-footer
                                no-data-text="Nessun terzista presente">
                                <template v-slot:item.fornitore="{ item }">
                                    <v-text-field v-model="item.fornitore" dense hide-details outlined />
                                </template>
                                <template v-slot:item.numBolla="{ item }">
                                    <v-text-field v-model="item.numBolla" dense hide-details outlined />
                                </template>
                                <template v-slot:item.dataBolla="{ item }">
                                    <v-text-field v-model="item.dataBolla" type="date" dense hide-details outlined />
                                </template>
                                <template v-slot:item.descrizione="{ item }">
                                    <v-text-field v-model="item.descrizione" dense hide-details outlined />
                                </template>
                                <template v-slot:item.actions="{ item }">
                                    <v-btn icon color="error" @click="removeTerzista('ric', item)">
                                        <v-icon>delete</v-icon>
                                    </v-btn>
                                </template>
                            </v-data-table>
                        </v-card>
                    </div>
                </v-form>
            </v-card-text>

            <v-divider />

            <v-card-actions>
                <v-btn icon color="error" @click="eliminaCommessa" :disabled="!(elemento.id > 0) || salvataggioInCorso">
                    <v-icon>delete</v-icon>
                </v-btn>
                <v-spacer />
                <v-btn @click="handleEscClose" text>Annulla</v-btn>
                <v-btn color="primary" @click="salva" :disabled="salvataggioInCorso" :loading="salvataggioInCorso">
                    Salva
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { get_dichiarazioni, get_gruppi_prodotto, get_um, get_essenze } from '@/api/api.registroSintesi.js'
import { get_materiali_disponibili, set_commessa, del_commessa } from '@/api/api.registroSintesiPers.js'

const arrotonda = (v, d = 3) => Math.round((Number(v) || 0) * Math.pow(10, d)) / Math.pow(10, d);
const numeroOppureNull = (v) => (v === '' || v === null || v === undefined || isNaN(Number(v))) ? null : Number(v);
const oggi = () => new Date().toISOString().substring(0, 10);

export default {
    props: {
        dialog: { type: Boolean, default: false },
        compCode: { type: String, default: '' },
        editItem: { type: Object, default: () => ({}) },
        variante: { type: String, default: '' },    // "CARTONE" | "LEGNO"
    },

    data() {
        return {
            elemento: this.elementoVuoto(),
            formValid: false,
            salvataggioInCorso: false,

            gruppiProdotto: [],
            dichiarazioniFSC: [],
            um: [],
            essenze: [],

            datiMatPrime: [],
            // giacenza disponibile per materiale, comprensiva di quanto questa commessa ha gia' prelevato (in modifica)
            baseRimanente: {},

            headersDDT: [
                { text: "Num. Bolla", value: "numBolla", width: "200px" },
                { text: "Data Bolla", value: "dataBolla", width: "200px" },
                { text: "Note (opzionali)", value: "descrizione" },
                { text: "", value: "actions", sortable: false, align: "center", width: "80px" },
            ],
            headersFatture: [
                { text: "Num. Documento", value: "numDocumento", width: "200px" },
                { text: "Data Documento", value: "dataDocumento", width: "200px" },
                { text: "Note (opzionali)", value: "note" },
                { text: "", value: "actions", sortable: false, align: "center", width: "80px" },
            ],
            headersTerzisti: [
                { text: "Terzista", value: "fornitore" },
                { text: "Num. Bolla", value: "numBolla", width: "150px" },
                { text: "Data Bolla", value: "dataBolla", width: "150px" },
                { text: "Descrizione (opzionale)", value: "descrizione" },
                { text: "", value: "actions", sortable: false, align: "center", width: "80px" },
            ],

            rules: {
                required: v => (v !== null && v !== undefined && v !== '') || 'Campo obbligatorio',
                numeroPositivo: v => {
                    if (v === null || v === undefined || v === '') return true;
                    return v > 0 || 'Deve essere un numero positivo';
                }
            }
        };
    },

    computed: {
        role() {
            return this.$store.getters['user/getRole'];
        },

        // Nei gruppi con inputOutput non valorizzato la scelta e' libera
        gruppiOutput() {
            return this.gruppiProdotto.filter(g => g.inputOutput !== 'I');
        },

        headers() {
            const intestazioni = [
                { text: "Gruppo", value: "codGruppoCategoriaFSC" },
                { text: "Fornitore", value: "fornitore" },
                { text: "DDT", value: "nBollaText" },
                { text: "Descrizione", value: "descrizione" },
            ];
            if (this.variante === 'LEGNO')
                intestazioni.push({ text: "Essenza", value: "essenza" });
            intestazioni.push(
                { text: "Disponibile", value: "disponibile", sortable: false },
                { text: "UM", value: "um" },
                { text: "Prelievo", value: "qtaPrelievo", sortable: false },
            );
            return intestazioni;
        },
    },

    methods: {
        elementoVuoto() {
            return { companyCode: '', id: -1, riferimento: '', clienteUscita: '', note: '', ddt: [], fatture: [], terzistiInv: [], terzistiRic: [], sezioni: [] };
        },

        async handleEscClose() {
            const confirm = await this.$confirm.open(
                "Chiudi",
                "Chiudere la finestra? Le modifiche non salvate andranno perse"
            );
            if (confirm) {
                this.chiudi(false);
            }
        },

        chiudi(aggiorna) {
            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }
            this.$emit('close', aggiorna);
        },

        async eliminaCommessa() {
            if (!(this.elemento.id > 0)) {
                this.$toast.alert("Nessuna commessa da eliminare");
                return;
            }

            const confirm = await this.$confirm.open(
                "Elimina",
                "Confermare l'eliminazione di tutti i dati di questa vendita?"
            );
            if (!confirm) return;

            this.salvataggioInCorso = true;
            try {
                const res = await del_commessa({ companyCode: this.compCode, id: this.elemento.id });
                if (res.data === "OK") {
                    this.chiudi(true);
                } else {
                    this.$toast.error("Errore durante l'eliminazione: " + res.data);
                }
            } catch (error) {
                console.error("Errore durante l'eliminazione:", error);
                this.$toast.error("Errore durante l'eliminazione della commessa");
            } finally {
                this.salvataggioInCorso = false;
            }
        },

        //#region Caricamento

        async caricaDati() {
            try {
                // Gli elenchi si caricano prima di costruire le righe: servono per la giacenza disponibile
                await Promise.all([this.caricaDatiCombo(), this.caricaDatiMatPrime()]);
                this.preparaSezioni();
            } catch (error) {
                console.error("Errore durante il caricamento dei dati:", error);
                this.$toast.error("Errore durante il caricamento dei dati");
            }
        },

        async caricaDatiCombo() {
            const par = this.role !== "utente" ? "?companyCode=" + this.compCode : "";

            const [gruppiRes, dichiarazioniRes, umRes, essenzeRes] = await Promise.all([
                get_gruppi_prodotto(par),
                get_dichiarazioni(par),
                get_um(),
                get_essenze()
            ]);

            this.gruppiProdotto = gruppiRes.data || [];
            this.dichiarazioniFSC = dichiarazioniRes.data || [];
            this.um = umRes.data || [];
            this.essenze = essenzeRes.data || [];
        },

        async caricaDatiMatPrime() {
            const par = this.role !== "utente" ? "?companyCode=" + this.compCode : "";
            const res = await get_materiali_disponibili(par);
            this.datiMatPrime = res.data || [];
        },

        /**
         * Costruisce le righe di materiale di ogni gruppo di prodotto. In modifica il server restituisce la giacenza
         * al netto anche dei consumi di questa commessa, che qui vengono riaggiunti.
         * Le righe selezionate sono gli stessi oggetti dell'elenco, cosi' il prelievo digitato resta allineato.
         */
        preparaSezioni() {
            const vecchi = {};
            this.elemento.sezioni.forEach(s => (s.selected || []).forEach(m => {
                vecchi[m.idMateriale] = arrotonda((vecchi[m.idMateriale] || 0) + m.qtaPrelievo);
            }));

            const base = {};
            this.datiMatPrime.forEach(m => { base[m.idMateriale] = arrotonda(m.qtaRimanente + (vecchi[m.idMateriale] || 0)); });
            this.baseRimanente = base;

            this.elemento.sezioni = this.elemento.sezioni.map(s => this.sezioneUI(s));
        },

        sezioneUI(s) {
            const salvati = s.selected || [];
            const righe = this.datiMatPrime
                .filter(m => this.baseRimanente[m.idMateriale] > 0 || salvati.some(x => x.idMateriale === m.idMateriale))
                .map(m => {
                    const sv = salvati.find(x => x.idMateriale === m.idMateriale);
                    return { ...m, qtaPrelievo: sv ? sv.qtaPrelievo : 0 };
                });

            return {
                id: s.id || Math.floor(Date.now() + Math.random()),
                gruppoCategoriaFSC: s.gruppoCategoriaFSC && s.gruppoCategoriaFSC.codGruppo ? s.gruppoCategoriaFSC : null,
                dichiarazioneFSC: s.dichiarazioneFSC && s.dichiarazioneFSC.idDichiarazione ? s.dichiarazioneFSC : null,
                quantita: s.quantita || null,
                um: s.um || (this.variante === 'LEGNO' ? 'ml' : 'pz'),
                essenza: s.essenza || null,
                dataUscita: s.dataUscita || oggi(),
                sezioneBase: s.sezioneBase || null,
                sezioneAltezza: s.sezioneAltezza || null,
                resa: s.resa || null,
                codiceProdotto: s.codiceProdotto || '',
                codiceImpiantoStampa: s.codiceImpiantoStampa || '',
                elencoMateriePrime: righe,
                selected: righe.filter(r => salvati.some(x => x.idMateriale === r.idMateriale)),
            };
        },

        //#endregion

        //#region Gruppi di prodotto e materiali

        aggiungiSezione() {
            this.elemento.sezioni.push(this.sezioneUI({}));
        },

        async rimuoviSezione(index) {
            const confirm = await this.$confirm.open("Elimina", `Eliminare il gruppo di prodotto ${index + 1}?`);
            if (!confirm) return;
            this.elemento.sezioni.splice(index, 1);
        },

        // Quantita' prelevata dalle altre righe di output della stessa commessa per lo stesso materiale
        prelevatoAltrove(idMateriale, sezione) {
            let tot = 0;
            this.elemento.sezioni.forEach(s => {
                if (s === sezione) return;
                s.selected.forEach(r => { if (r.idMateriale === idMateriale) tot += Number(r.qtaPrelievo) || 0; });
            });
            return tot;
        },

        disponibile(item, sezione) {
            const base = this.baseRimanente[item.idMateriale] || 0;
            return Math.max(0, arrotonda(base - this.prelevatoAltrove(item.idMateriale, sezione)));
        },

        // Alla selezione propone l'intera giacenza disponibile (il caso piu' comune e' il consumo del lotto intero)
        aggiornaPrelievi(sezione) {
            sezione.elencoMateriePrime.forEach(r => {
                const selezionata = sezione.selected.some(s => s.idMateriale === r.idMateriale);
                if (!selezionata) r.qtaPrelievo = 0;
                else if (!(r.qtaPrelievo > 0)) r.qtaPrelievo = this.disponibile(r, sezione);
            });
        },

        validaPrelievo(item, sezione) {
            const max = this.disponibile(item, sezione);
            if (item.qtaPrelievo === null || item.qtaPrelievo === '' || item.qtaPrelievo < 0) item.qtaPrelievo = 0;
            else if (item.qtaPrelievo > max) item.qtaPrelievo = max;
        },

        //#endregion

        //#region Anteprima calcolo

        /**
         * Anteprima di scarto e fattore di conversione mentre si compila. Le formule sono quelle dei registri dei
         * clienti e vanno tenute allineate con CalcoliRegistroPers.cs, che e' quella usata per l'export.
         */
        calcolo(sezione) {
            const input = sezione.selected.reduce((tot, r) => tot + (Number(r.qtaPrelievo) || 0), 0);
            const q = Number(sezione.quantita) || 0;

            if (this.variante === 'LEGNO') {
                const base = Number(sezione.sezioneBase) || 0;
                const altezza = Number(sezione.sezioneAltezza) || 0;
                if (base > 0 && altezza > 0 && q > 0) {
                    const netto = (base / 1000) * (altezza / 1000) * q;
                    const scarto = input - netto;
                    return { input, netto, scarto, fc: input > 0 ? scarto / input : null, ok: input > 0, passante: false };
                }
                return { input, netto: input, scarto: 0, fc: 1, ok: input > 0, passante: true };
            }

            const resa = Number(sezione.resa) || 0;
            if (resa > 0 && input > 0 && q > 0) {
                const utilizzati = q / resa;
                const scarto = input - utilizzati;
                const perc = scarto / input;
                return { input, netto: utilizzati, scarto, perc, fc: 1 - perc, ok: true };
            }
            return { input, ok: false };
        },

        fmt(v, decimali = 3) {
            if (v === null || v === undefined || isNaN(v)) return '-';
            return Number(v).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: decimali });
        },

        righeCalcolo(sezione) {
            const c = this.calcolo(sezione);
            if (!c.ok) return [];
            if (this.variante === 'LEGNO') {
                return [
                    { label: 'mc prelevati', value: this.fmt(c.input) },
                    { label: 'mc prodotti', value: this.fmt(c.netto, 4) },
                    { label: 'Scarto (mc)', value: this.fmt(c.scarto, 4), warn: c.scarto < 0 },
                    { label: 'Fattore di conversione', value: this.fmt(c.fc, 4) },
                ];
            }
            return [
                { label: 'Fogli considerati', value: this.fmt(c.input) },
                { label: 'Fogli utilizzati', value: this.fmt(c.netto) },
                { label: 'Scarto (fogli)', value: this.fmt(c.scarto), warn: c.scarto < 0 },
                { label: 'Scarto %', value: this.fmt(c.perc * 100, 2) + ' %' },
                { label: 'FC', value: this.fmt(c.fc, 4) },
            ];
        },

        noteCalcolo(sezione) {
            const c = this.calcolo(sezione);
            if (this.variante === 'LEGNO' && c.ok && c.passante)
                return "Sezione non indicata: il materiale passa invariato (scarto 0, fattore di conversione 1).";
            if (c.ok && c.scarto < 0)
                return "Attenzione: la quantità prodotta supera il materiale prelevato.";
            return "";
        },

        //#endregion

        //#region DDT, fatture e terzisti

        addDDT() {
            this.elemento.ddt.push({ LocalId: Date.now() + Math.random(), prog: -1, numBolla: "", dataBolla: oggi(), descrizione: "" });
        },

        removeDDT(item) {
            this.elemento.ddt = this.elemento.ddt.filter(d => d.LocalId !== item.LocalId);
        },

        addFattura() {
            this.elemento.fatture.push({ LocalId: Date.now() + Math.random(), prog: -1, numDocumento: "", dataDocumento: oggi(), note: "" });
        },

        removeFattura(item) {
            this.elemento.fatture = this.elemento.fatture.filter(f => f.LocalId !== item.LocalId);
        },

        addTerzista(tipo) {
            const lista = tipo === 'inv' ? this.elemento.terzistiInv : this.elemento.terzistiRic;
            lista.push({ LocalId: Date.now() + Math.random(), prog: -1, fornitore: "", numBolla: "", dataBolla: oggi(), descrizione: "" });
        },

        removeTerzista(tipo, item) {
            if (tipo === 'inv') this.elemento.terzistiInv = this.elemento.terzistiInv.filter(t => t.LocalId !== item.LocalId);
            else this.elemento.terzistiRic = this.elemento.terzistiRic.filter(t => t.LocalId !== item.LocalId);
        },

        //#endregion

        //#region Salvataggio

        async salva() {
            if (this.$refs.form && !this.$refs.form.validate()) {
                this.$toast.alert("Compilare tutti i campi obbligatori");
                return;
            }
            if (!this.validaCommessa()) return;

            this.salvataggioInCorso = true;
            try {
                const res = await set_commessa(this.preparaDaSalvare());
                if (res.data === "OK") {
                    this.chiudi(true);
                } else {
                    this.$toast.error("Errore durante il salvataggio: " + res.data);
                }
            } catch (error) {
                console.error("Errore durante il salvataggio:", error);
                this.$toast.error("Errore durante il salvataggio della commessa");
            } finally {
                this.salvataggioInCorso = false;
            }
        },

        // Invia solo cio' che serve al server: niente elenchi di materiali e calcoli di anteprima
        preparaDaSalvare() {
            const e = this.elemento;
            return {
                companyCode: this.compCode,
                id: e.id,
                riferimento: e.riferimento,
                clienteUscita: e.clienteUscita,
                note: e.note,
                ddt: e.ddt,
                fatture: e.fatture,
                terzistiInv: e.terzistiInv,
                terzistiRic: e.terzistiRic,
                sezioni: e.sezioni.map(s => ({
                    gruppoCategoriaFSC: s.gruppoCategoriaFSC,
                    dichiarazioneFSC: s.dichiarazioneFSC,
                    quantita: Number(s.quantita) || 0,
                    um: s.um,
                    essenza: this.variante === 'LEGNO' ? s.essenza : null,
                    dataUscita: s.dataUscita,
                    sezioneBase: numeroOppureNull(s.sezioneBase),
                    sezioneAltezza: numeroOppureNull(s.sezioneAltezza),
                    resa: numeroOppureNull(s.resa),
                    codiceProdotto: s.codiceProdotto,
                    codiceImpiantoStampa: s.codiceImpiantoStampa,
                    selected: s.selected.map(r => ({
                        idMateriale: r.idMateriale,
                        qta: r.qta,
                        qtaPrelievo: Number(r.qtaPrelievo) || 0,
                        um: r.um,
                    })),
                })),
            };
        },

        validaCommessa() {
            const c = this.elemento;
            if (!c.riferimento || c.riferimento.trim() === "") {
                this.$toast.alert("Il campo Riferimento è obbligatorio");
                return false;
            }
            if (!c.clienteUscita || c.clienteUscita.trim() === "") {
                this.$toast.alert("Il campo Cliente è obbligatorio");
                return false;
            }
            if (c.sezioni.length === 0) {
                this.$toast.alert("È necessario inserire almeno un gruppo di prodotto");
                return false;
            }

            for (let i = 0; i < c.sezioni.length; i++) {
                const s = c.sezioni[i];
                const n = i + 1;

                if (!s.gruppoCategoriaFSC) return this.errore(`Gruppo prodotto mancante nella sezione ${n}`);
                if (!s.dichiarazioneFSC) return this.errore(`Dichiarazione FSC mancante nella sezione ${n}`);
                if (!s.um) return this.errore(`Unità di misura mancante nella sezione ${n}`);
                if (!s.quantita || s.quantita <= 0) return this.errore(`Quantità non valida nella sezione ${n}`);
                if (!s.dataUscita) return this.errore(`Data uscita mancante nella sezione ${n}`);

                if (this.variante === 'LEGNO') {
                    const base = Number(s.sezioneBase) > 0, altezza = Number(s.sezioneAltezza) > 0;
                    if (base !== altezza) return this.errore(`Nella sezione ${n} indicare sia la base sia l'altezza, oppure nessuna delle due`);
                } else if (!(Number(s.resa) > 0)) {
                    return this.errore(`Resa mancante nella sezione ${n}`);
                }

                if (s.selected.length === 0) return this.errore(`Nessun materiale selezionato nella sezione ${n}`);
                if (s.selected.some(r => !(Number(r.qtaPrelievo) > 0)))
                    return this.errore(`Indicare la quantità prelevata per tutti i materiali selezionati nella sezione ${n}`);
            }

            const controllaDate = (elenco, campo, nome) => {
                for (let i = 0; i < elenco.length; i++) {
                    if (!elenco[i][campo]) return this.errore(`Data mancante: ${nome} ${i + 1}`);
                }
                return true;
            };
            return controllaDate(c.fatture, 'dataDocumento', 'fattura')
                && controllaDate(c.ddt, 'dataBolla', 'DDT')
                && controllaDate(c.terzistiInv, 'dataBolla', 'terzista inviato')
                && controllaDate(c.terzistiRic, 'dataBolla', 'terzista ricevuto');
        },

        errore(messaggio) {
            this.$toast.alert(messaggio);
            return false;
        },

        //#endregion
    },

    watch: {
        dialog(aperto) {
            if (!aperto) return;

            const e = JSON.parse(JSON.stringify(this.editItem || {}));
            this.elemento = { ...this.elementoVuoto(), ...e, companyCode: this.compCode };
            this.elemento.sezioni = this.elemento.sezioni || [];

            this.caricaDati();
            this.$nextTick(() => {
                if (this.$refs.form) {
                    this.$refs.form.resetValidation();
                }
            });
        }
    },
};
</script>

<style scoped>
.v-data-table ::v-deep .v-data-table__wrapper {
    overflow-x: auto;
}
</style>
